import { useState, useEffect } from 'react';
import { tenantsAPI, storesAPI, regionsAPI } from '../services/api';
import { Store, Plus, Edit2, RefreshCw, Copy } from 'lucide-react';
import toast from 'react-hot-toast';

function StoreModal({ store, tenantId, regions, onClose, onSaved }) {
  const [form, setForm] = useState(store || { name: '', region_id: '', address: '', city: '', country_code: '', timezone: 'Africa/Johannesburg' });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...form, region_id: form.region_id || null };
      if (store) await storesAPI.update(tenantId, store.id, data);
      else await storesAPI.create(tenantId, data);
      toast.success(store ? 'Store updated' : 'Store created');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving store');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">{store ? 'Edit Store' : 'New Store'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Store Name</label>
              <input className="form-input" value={form.name} required onChange={e => set('name', e.target.value)} placeholder="e.g. Cape Town CBD" />
            </div>
            <div className="form-group">
              <label className="form-label">Region</label>
              <select className="form-select" value={form.region_id} onChange={e => set('region_id', e.target.value)}>
                <option value="">No region</option>
                {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Street address" />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} placeholder="Cape Town" />
            </div>
            <div className="form-group">
              <label className="form-label">Country Code</label>
              <input className="form-input" value={form.country_code} onChange={e => set('country_code', e.target.value)} placeholder="ZA" maxLength={3} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Timezone</label>
            <select className="form-select" value={form.timezone} onChange={e => set('timezone', e.target.value)}>
              <option value="Africa/Johannesburg">Africa/Johannesburg (SAST)</option>
              <option value="Africa/Cape_Town">Africa/Cape_Town</option>
              <option value="UTC">UTC</option>
              <option value="Europe/London">Europe/London</option>
              <option value="America/New_York">America/New_York</option>
            </select>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : store ? 'Save Changes' : 'Create Store'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Stores() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [stores, setStores] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    tenantsAPI.list().then(r => {
      setTenants(r.data);
      if (r.data.length > 0) setSelectedTenant(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedTenant) return;
    setLoading(true);
    Promise.all([
      storesAPI.list(selectedTenant),
      regionsAPI.list(selectedTenant),
    ]).then(([s, r]) => {
      setStores(s.data);
      setRegions(r.data);
    }).finally(() => setLoading(false));
  }, [selectedTenant]);

  const regenCode = async (store) => {
    try {
      await storesAPI.regeneratePairingCode(selectedTenant, store.id);
      toast.success('Pairing code regenerated');
      storesAPI.list(selectedTenant).then(r => setStores(r.data));
    } catch { toast.error('Failed to regenerate code'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Pairing code copied');
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Stores</h1>
          <p className="page-subtitle">Manage store locations and device pairing codes</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select className="form-select" style={{ width: 200 }} value={selectedTenant} onChange={e => setSelectedTenant(e.target.value)}>
            <option value="">Select brand...</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="btn btn-primary" onClick={() => setModal('new')} disabled={!selectedTenant}>
            <Plus size={16} /> New Store
          </button>
        </div>
      </div>

      {!selectedTenant ? (
        <div className="card">
          <div className="empty-state">
            <Store size={48} />
            <h3>Select a brand</h3>
            <p>Choose a brand from the dropdown to view its stores</p>
          </div>
        </div>
      ) : loading ? (
        <div className="loading-state"><div className="spinner" /></div>
      ) : stores.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Store size={48} />
            <h3>No stores yet</h3>
            <p>Create your first store for this brand</p>
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setModal('new')}>
              <Plus size={16} /> Create Store
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Store Name</th>
                  <th>Location</th>
                  <th>Region</th>
                  <th>Pairing Code</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stores.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      {[s.city, s.country_code].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      {regions.find(r => r.id === s.region_id)?.name || '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="pairing-code" style={{ fontSize: 14, padding: '6px 12px' }}>{s.pairing_code}</span>
                        <button className="btn btn-ghost btn-sm" onClick={() => copyCode(s.pairing_code)} title="Copy">
                          <Copy size={12} />
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${s.is_active ? 'green' : 'red'}`}>
                        {s.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setModal(s)}>
                          <Edit2 size={12} /> Edit
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => regenCode(s)} title="New pairing code">
                          <RefreshCw size={12} /> Regen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <StoreModal
          store={modal === 'new' ? null : modal}
          tenantId={selectedTenant}
          regions={regions}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); storesAPI.list(selectedTenant).then(r => setStores(r.data)); }}
        />
      )}
    </div>
  );
}
